import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { AppShell } from "@/App"
import { listOrders, type OrderRow } from "@/lib/api"
import { useOrderSources } from "@/lib/sources"
import { SourceBadge } from "@/components/source-badge"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { StepDots } from "@/components/steps"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ShoppingCart, RefreshCw, Settings as SettingsIcon, Search, X, Boxes, Truck } from "lucide-react"

interface HomeProps {
  shell: AppShell | null
}

type OrderFilter = "open" | "closed"

export function Home({ shell }: HomeProps) {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<OrderFilter>("open")
  const [search, setSearch] = useState("")
  const [sourceFilter, setSourceFilter] = useState<string>("all")
  const sources = useOrderSources(shell)
  const [nextActionFilter, setNextActionFilter] = useState<string>("all")

  async function load() {
    if (!shell) return
    try {
      const rows = await listOrders(shell, 50)
      setOrders(rows)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    const t = setInterval(() => { void load() }, 5000)
    return () => clearInterval(t)
  }, [shell])

  if (!shell) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Encomendas Shopify</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Standalone dev (sem token). Abre via <code className="rounded bg-muted px-1 py-0.5">/pt/apps/shopify-fulfillment</code> no host para ver dados reais.
        </p>
      </div>
    )
  }

  const q = search.trim().toLowerCase()
  const matchesSearch = (o: OrderRow): boolean => {
    if (sourceFilter !== "all" && sources.sectionOf(o) !== sourceFilter) return false
    if (!q) return true
    const haystack = [
      o.shopify_order_number,
      o.customer_name,
      o.customer_email,
      o.customer_nif,
      o.ship_city,
      o.ship_zip,
      o.next_action,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
    return haystack.includes(q)
  }
  const openOrders = orders.filter((o) => !o.completed_at && matchesSearch(o))
  const closedOrders = orders.filter((o) => o.completed_at && matchesSearch(o))
  const baseOrders = filter === "open" ? openOrders : closedOrders

  // Distinct "Próxima acção" values present in the current tab (+search), with
  // counts — drives the next-action filter dropdown.
  const nextActionCounts = new Map<string, number>()
  for (const o of baseOrders) {
    const na = (o.next_action ?? "").trim()
    if (na) nextActionCounts.set(na, (nextActionCounts.get(na) ?? 0) + 1)
  }
  const nextActionOptions = Array.from(nextActionCounts.entries()).sort((a, b) =>
    a[0].localeCompare(b[0], "pt"),
  )
  // Reset the filter if the selected action no longer exists in the tab.
  const activeNextAction =
    nextActionFilter !== "all" && !nextActionCounts.has(nextActionFilter)
      ? "all"
      : nextActionFilter

  const visibleOrders =
    activeNextAction === "all"
      ? baseOrders
      : baseOrders.filter((o) => (o.next_action ?? "").trim() === activeNextAction)

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <ShoppingCart className="size-5 text-muted-foreground" />
          <div>
            <h1 className="text-lg font-semibold leading-tight">Encomendas</h1>
            <p className="text-xs text-muted-foreground">
              {openOrders.length} em aberto · {closedOrders.length} fechada{closedOrders.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <RefreshCw className="size-3.5 animate-spin-slow" />
            <span>Refresh 5s</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/logistica")}>
            <Boxes className="mr-1.5 size-3.5" />
            Logística
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/tracking")}>
            <Truck className="mr-1.5 size-3.5" />
            Tracking
          </Button>
          {shell && shell.role >= 10 && (
            <Button variant="outline" size="sm" onClick={() => navigate("/settings")}>
              <SettingsIcon className="mr-1.5 size-3.5" />
              Settings
            </Button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {/* Filtro + pesquisa */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-md border p-0.5">
            <button
              type="button"
              onClick={() => setFilter("open")}
              className={
                "rounded px-3 py-1 text-sm font-medium transition-colors " +
                (filter === "open"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-accent")
              }
            >
              Em aberto ({openOrders.length})
            </button>
            <button
              type="button"
              onClick={() => setFilter("closed")}
              className={
                "rounded px-3 py-1 text-sm font-medium transition-colors " +
                (filter === "closed"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-accent")
              }
            >
              Fechadas ({closedOrders.length})
            </button>
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            {/* Filtro por fonte (secção do ETICDATA) */}
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-9 rounded-md border bg-background px-2 text-sm text-muted-foreground"
              title="Filtrar por fonte"
            >
              <option value="all">Todas as fontes</option>
              {sources.options.map((s) => (
                <option key={s.section} value={s.section}>
                  {s.name}
                </option>
              ))}
            </select>
            {/* Filtro por Próxima acção */}
            <select
              value={activeNextAction}
              onChange={(e) => setNextActionFilter(e.target.value)}
              className="h-9 max-w-[16rem] rounded-md border bg-background px-2 text-sm text-muted-foreground"
              title="Filtrar por próxima acção"
            >
              <option value="all">Todas as acções ({baseOrders.length})</option>
              {nextActionOptions.map(([na, count]) => (
                <option key={na} value={na}>
                  {na} ({count})
                </option>
              ))}
            </select>
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Pesquisar (nº, cliente, cidade…)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 pr-8 text-sm"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent"
                  aria-label="Limpar"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <Card className="mb-4 border-destructive/30 bg-destructive/5">
            <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {loading && orders.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">A carregar…</CardContent>
          </Card>
        )}

        {!loading && orders.length === 0 && !error && (
          <Card>
            <CardHeader>
              <CardTitle>Sem encomendas ainda</CardTitle>
              <CardDescription>
                As encomendas aparecem aqui assim que o Shopify emite o webhook <code>orders/paid</code> para{" "}
                <code className="font-mono">/api/apps/shopify-fulfillment/webhook/orders-paid</code>.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {!loading && orders.length > 0 && visibleOrders.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              {filter === "open" ? "Nenhuma encomenda em aberto." : "Nenhuma encomenda fechada."}
            </CardContent>
          </Card>
        )}

        {visibleOrders.length > 0 && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Cidade</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Pipeline</TableHead>
                  <TableHead>Próxima acção</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleOrders.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/order/${row.id}`)}
                  >
                    <TableCell className="font-mono text-xs">
                      {(() => {
                        // Alert: orders paid more than 24h ago that aren't
                        // closed yet. created_at == webhook fire == payment
                        // moment (orders/paid). completed_at non-null means
                        // expedição done (final state).
                        const created = new Date(row.created_at).getTime()
                        const ageMs = Number.isFinite(created)
                          ? Date.now() - created
                          : 0
                        const isStale = !row.completed_at && ageMs > 24 * 60 * 60 * 1000
                        // "ENCCL 112426/734" → "112426/734": o badge de origem já diz o tipo.
                        const label = row.shopify_order_number?.replace(/^ENCCL\s+/, "") ?? row.id.slice(0, 8)
                        return (
                          <span
                            className={
                              isStale
                                ? "inline-flex items-center gap-1 rounded-md border border-orange-400 bg-orange-50 px-1.5 py-0.5 font-semibold text-orange-800"
                                : ""
                            }
                            title={
                              isStale
                                ? `Sem fecho há ${Math.floor(ageMs / (60 * 60 * 1000))}h após pagamento`
                                : undefined
                            }
                          >
                            <span className="mr-1.5 align-middle">
                              <SourceBadge size="sm" name={sources.sourceName(row)} section={sources.sectionOf(row)} />
                            </span>
                            {isStale && <span aria-hidden>⚠</span>}
                            {label}
                          </span>
                        )
                      })()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                      {(() => {
                        const d = new Date(row.created_at)
                        if (Number.isNaN(d.getTime())) return "—"
                        const dd = String(d.getDate()).padStart(2, "0")
                        const mm = String(d.getMonth() + 1).padStart(2, "0")
                        const yyyy = d.getFullYear()
                        const hh = String(d.getHours()).padStart(2, "0")
                        const mi = String(d.getMinutes()).padStart(2, "0")
                        return (
                          <>
                            <div>{`${dd}/${mm}/${yyyy}`}</div>
                            <div className="text-[10px]">{`${hh}:${mi}`}</div>
                          </>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{row.customer_name ?? "—"}</span>
                        {row.customer_nif && (
                          <span className="text-[11px] text-muted-foreground">NIF {row.customer_nif}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.ship_city ?? "—"}
                      {row.ship_zip && <span className="ml-1 text-[11px] text-muted-foreground">{row.ship_zip}</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.total_amount != null ? `${Number(row.total_amount).toFixed(2)} ${row.currency ?? "EUR"}` : "—"}
                    </TableCell>
                    <TableCell>
                      <StepDots states={row.step_states} currentStep={row.current_step} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.completed_at ? (
                          <Badge variant="success">Concluída</Badge>
                        ) : row.next_action ? (
                          <span className="inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            {row.next_action}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {/* Cliente já foi notificado de atraso. */}
                        {row.delay_notified_at && (
                          <Badge variant="destructive">Atraso · {row.delay_eta ?? "—"}</Badge>
                        )}
                        {row.shipping_quote_required_at && !row.shipping_quote_paid_at && (
                          <Badge variant="warning">Aguarda portes</Badge>
                        )}
                        {row.cancelled_at ? (
                          <Badge variant="destructive">Cancelada no Shopify</Badge>
                        ) : row.enc_update_pending ? (
                          <Badge variant="warning">Atualizar ENC</Badge>
                        ) : row.change_alert ? (
                          <Badge variant="warning">Alterada no Shopify</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  )
}
